import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Filters } from "@/types";

export interface PaginationData {
    limit: number;
    count: number;
    page: number;
    last: number;
    offset: number;
    from: number;
    to: number;
    in: number;
    previous: number | null;
    next: number | null;
}

interface PaginationProps {
    data: PaginationData;
    filters: Filters;
    onPageChange: (page: number) => void
}

export function Pagination({ data, onPageChange }: PaginationProps) {
    const { page, last, from, to, count, previous, next } = data

    const getVisiblePages = () => {
        const pages: (number | string)[] = []
        const maxVisible = 5

        if (last <= maxVisible + 2) {
            for (let i = 1; i <= last; i++) {
                pages.push(i)
            }
        } else {
            pages.push(1)

            if (page > 3) {
                pages.push("...")
            }

            const start = Math.max(2, page - 1)
            const end = Math.min(last - 1, page + 1)

            for (let i = start; i <= end; i++) {
                if (i !== 1 && i !== last) {
                    pages.push(i)
                }
            }

            if (page < last - 2) {
                pages.push("...")
            }

            pages.push(last)
        }

        return pages
    }

    const visiblePages = getVisiblePages()

    return (
        <div className="flex flex-col items-center gap-4 py-8">
            <p className="text-sm text-muted-foreground">
                Wyświetlanie <span className="font-medium text-foreground">{from}</span> - <span className="font-medium text-foreground">{to}</span> z <span className="font-medium text-foreground">{count.toLocaleString("pl-PL")}</span> produktów
            </p>

            <div className="flex items-center gap-1">
                <Button
                    variant="outline"
                    size="icon"
                    onClick={() => onPageChange(1)}
                    disabled={page === 1}
                    className="h-9 w-9"
                    aria-label="Pierwsza strona"
                >
                    <ChevronsLeft className="h-4 w-4" />
                </Button>

                <Button
                    variant="outline"
                    size="icon"
                    onClick={() => previous && onPageChange(previous)}
                    disabled={!previous}
                    className="h-9 w-9"
                    aria-label="Poprzednia strona"
                >
                    <ChevronLeft className="h-4 w-4" />
                </Button>

                <div className="flex items-center gap-1 px-2">
                    {visiblePages.map((pageNum, index) => (
                        pageNum === "..." ? (
                            <span key={`ellipsis-${index}`} className="px-2 text-muted-foreground">
                                ...
                            </span>
                        ) : (
                            <Button
                                key={pageNum}
                                variant={pageNum === page ? "default" : "outline"}
                                size="icon"
                                onClick={() => onPageChange(pageNum as number)}
                                className="h-9 w-9"
                                aria-label={`Strona ${pageNum}`}
                                aria-current={pageNum === page ? "page" : undefined}
                            >
                                {pageNum}
                            </Button>
                        )
                    ))}
                </div>

                <Button
                    variant="outline"
                    size="icon"
                    onClick={() => next && onPageChange(next)}
                    disabled={!next}
                    className="h-9 w-9"
                    aria-label="Następna strona"
                >
                    <ChevronRight className="h-4 w-4" />
                </Button>

                <Button
                    variant="outline"
                    size="icon"
                    onClick={() => onPageChange(last)}
                    disabled={page === last}
                    className="h-9 w-9"
                    aria-label="Ostatnia strona"
                >
                    <ChevronsRight className="h-4 w-4" />
                </Button>
            </div>
            <div className="flex flex-col items-center gap-0 py-0">
            <p className="text-sm text-muted-foreground">
                Copyright © {new Date().getFullYear()} Wyszukiwarka Gadżetów. Wszystkie prawa zastrzeżone.
            </p>
            </div>
        </div>
    )
}
